import React, { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Card,
  Chip,
  Container,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { AdminPanelSettings, Refresh, Search } from '@mui/icons-material';
import { format } from 'date-fns';
import { fetchActivityLogs, fetchPaymentsActivityLogs } from '../hooks/hooks';

const PAGE_SIZE = 50;

const ACCOUNT_ACTIONS = [
  'login', 'logout', 'failed_login', 'data_change', 'api_request',
  'admin_action', 'db_modification', 'other',
];

const PAYMENTS_ACTIONS = [
  'api_request', 'payment_created', 'payment_updated', 'refund',
  'admin_action', 'login', 'logout', 'failed_login', 'db_modification', 'other',
];

const ACTION_CHIP_COLOR = {
  login: 'success',
  logout: 'default',
  failed_login: 'error',
  data_change: 'info',
  api_request: 'default',
  admin_action: 'warning',
  db_modification: 'info',
  payment_created: 'success',
  payment_updated: 'info',
  refund: 'warning',
  other: 'default',
};

const formatNum = (val) => Number(val || 0).toLocaleString();

function ActivityTable({ fetcher, queryKey, actions }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const borderClr = isDark ? 'rgba(255,255,255,0.09)' : '#E2E8F0';

  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(0);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [queryKey, page, actionFilter, search],
    queryFn: () => fetcher({
      page: page + 1,
      ...(actionFilter ? { action: actionFilter } : {}),
      ...(search ? { search } : {}),
    }),
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });

  const rows = Array.isArray(data?.results) ? data.results : [];
  const count = data?.count || 0;

  return (
    <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, borderRadius: '12px' }}>
      <Box sx={{ px: 2, pt: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1.5}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>
              Activity {isLoading ? '' : `(${formatNum(count)})`}
            </Typography>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => refetch()}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack direction="row" gap={1.5}>
            <TextField
              size="small"
              select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All Actions</MenuItem>
              {actions.map((a) => (
                <MenuItem key={a} value={a} sx={{ textTransform: 'capitalize' }}>
                  {a.replace('_', ' ')}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              placeholder="Search user, description..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <Search sx={{ fontSize: 18, mr: 0.75, color: 'text.disabled' }} /> }}
              sx={{ minWidth: 240 }}
            />
          </Stack>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mx: 2, mb: 2, borderRadius: '10px' }}>
          Failed to load activity. Please try refreshing.
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Time</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>IP Address</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">Loading activity...</Typography>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">No activity found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    <Typography fontSize={12.5}>
                      {log.timestamp ? format(new Date(log.timestamp), 'MMM d, yyyy HH:mm') : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize={13}>{log.user || 'System'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={(log.action || 'other').replace('_', ' ')}
                      size="small"
                      color={ACTION_CHIP_COLOR[log.action] || 'default'}
                      sx={{ fontSize: 11, textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography fontSize={13} noWrap sx={{ maxWidth: 360 }}>{log.description}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize={12.5} fontFamily="monospace">{log.ip_address || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize={12.5}>{log.status_code || '—'}</Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {count > 0 && (
        <TablePagination
          component="div"
          count={count}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
        />
      )}
    </Card>
  );
}

const AdminActivity = () => {
  const [tab, setTab] = useState(0);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
            <AdminPanelSettings sx={{ color: '#0BA480', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>
              Activity Logs
            </Typography>
            <Chip label="Admin" size="small" color="success" sx={{ fontWeight: 600 }} />
          </Stack>
          <Typography color="text.secondary" fontSize={14}>
            Platform-wide account and payment activity across all users.
          </Typography>
        </Box>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Account Activity" />
        <Tab label="Payments Activity" />
      </Tabs>

      {tab === 0 && (
        <ActivityTable fetcher={fetchActivityLogs} queryKey="adminAccountActivity" actions={ACCOUNT_ACTIONS} />
      )}
      {tab === 1 && (
        <ActivityTable fetcher={fetchPaymentsActivityLogs} queryKey="adminPaymentsActivity" actions={PAYMENTS_ACTIONS} />
      )}
    </Container>
  );
};

export default AdminActivity;
